import Icon from "../icon/Icon";
import React, { FunctionComponent, useCallback, useEffect, useState } from "react";
import "./Accordion.scss";
import classNames from "classnames";
import { useSpring, animated } from "react-spring";
import useMeasure from "react-use-measure";
import useFetch from "use-http";
import VoteButton from "../buttons/voteButton/VoteButton";
import { Feedback } from "components/faq/Faq";

type AccordionItem = {
  slug: string;
  title: string;
  content: string;

};

interface AccordionProps {
  handleActiveItem: (itemslug: string | undefined) => void;
  labels?: Feedback;
  activeItem?: string;
}

interface Props {
  items: AccordionItem[];
  handleActiveItem: (itemslug: string | undefined) => void;
  labels?: Feedback;
  activeItem?: string;
}

const AccordionItem = ({ slug, title, content, handleActiveItem, activeItem, labels }: AccordionProps & AccordionItem) => {
  const [isCollapsed, setIsCollapsed] = useState<string | undefined>(undefined);
  const [votingMessage, setVotingMessage] = useState<string | undefined>(undefined);
  const { post, response, loading, error } = useFetch(`/${document.documentElement.lang}/api/faq/items`);
  const [ref, bounds] = useMeasure();

  const toggleWrapperAnimatedStyle = useSpring({ transform: isCollapsed === slug ? "rotate(180deg)" : "rotate(0deg)", config: { duration: 200 } });
  const panelContentAnimatedStyle = useSpring({
    height: isCollapsed === slug ? bounds.height : 0,
    config: { duration: 100 },
  });

  const handleAccordion = useCallback(
    (slug: string) => {
      setIsCollapsed((prevState) => {
        if (slug === prevState) {
          handleActiveItem(undefined);
          return undefined;
        } else {
          handleActiveItem(slug);
          return slug;
        }
      });
    },
    [setIsCollapsed, handleActiveItem]
  );


  useEffect(() => {
    checkUrl();
  }, [window.location]);

  const checkUrl = () => {
    const query = new URLSearchParams(window.location.search);
    const queryItem = query.get("item");

    if (queryItem !== null && queryItem === slug) {
      handleAccordion(queryItem);
    }
  };

  const handleVoteClick = useCallback(async (voteType: string, slug: string) => {
    if (voteType === "up") {
      await post(`${slug}/helpful`);
    }
    if (voteType === "down") {
      await post(`${slug}/not_helpful`);
    }
    if (response.ok) {
      setVotingMessage(labels?.response);
    }
  }, [setVotingMessage, post]);

  return (
    <div className="accordion__item">
      <div className="accordion-item__head" onClick={() => handleAccordion(slug)}>
        <div className="accordion-item__title">{title}</div>
        <animated.div style={toggleWrapperAnimatedStyle}>
          <Icon name="arrowDown" className={classNames("accordion-item__icon")} />
        </animated.div>
      </div>
      <animated.div style={panelContentAnimatedStyle} className={classNames("accordion-item__content")}>
        <div ref={ref} className="accordion-item__innercontent">
          <div dangerouslySetInnerHTML={{ __html: content }} />
          <div className=" react-faq__label--light">{labels?.title}</div>
          {!votingMessage && (
            <div className="accordion-voting__holder">
              <VoteButton voteType="up" handleVoteClick={() => handleVoteClick("up", slug)} label={labels?.yes} />
              <VoteButton voteType="down" handleVoteClick={() => handleVoteClick("down", slug)} label={labels?.no} />
            </div>
          )}
          {votingMessage && <span className="react-faq__label">{votingMessage}</span>}
        </div>
      </animated.div>
    </div>
  );
};

const Accordion: FunctionComponent<Props> = ({ items, handleActiveItem, activeItem, labels }) => {
  return (
    <div className="accordion">
      {items.map((item) => {
        return <AccordionItem key={item.slug} slug={item.slug} title={item.title} content={item.content}activeItem={activeItem} handleActiveItem={handleActiveItem} labels={labels} />;
      })}
    </div>
  );
};

export default Accordion;
